import React, { useEffect, useMemo, useState } from 'react'
import { Fetcher } from '../../../Helpers/fetcher';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit'
import paginationFactory, { PaginationListStandalone, PaginationProvider, SizePerPageDropdownStandalone } from 'react-bootstrap-table2-paginator'
import BootstrapTable from 'react-bootstrap-table-next'
import { STATUS_CODE_TO_LABEL, STATUS_MAP, USER_ROLES } from '../../../Helpers/constant';
import { Notify } from '../../../Helpers/notification';
import { filterTableDataBySearch } from '../../../Helpers/utils';

const CampaignOperation = ({ app }) => {
    const apiPath = app.apiPath;
    const userId = app.userId;

    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [campaignList, setCampaignList] = useState([])
    const [selectedCampaign, setSelectedCampaign] = useState([])
    const [selectedFilter, setSelectedFilter] = useState("running")
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const isRunning = selectedFilter === "running"
    const actionLabel = isRunning ? "Pause" : "Resume";
    const isSubOrSuperAdmin=app.isSubOrSuperAdmin

    const getCampaigns = () => {
        const url = `/api/obd/get/running/paused/campaigns/${isRunning ? "running" : "paused"}/${userId}`
        setLoading(true);
        Fetcher.get(`${apiPath}${url}`)
            .then((res) => {
                if (res) {
                    const data = Array.isArray(res) ? res : []
                    setCampaignList(data)
                }
            })
            .catch(() => Notify.notify("Failed to fetch campaigns"))
            .finally(() => setLoading(false))
    }
    const handleSearch = (e) => {
        e.preventDefault();
        getCampaigns();
    }
    const handleSelect = (e) => {
        const value = e.target.value
        setSelectedFilter(value)
        setSelectedCampaign([])
    }

    useEffect(() => {
        getCampaigns()
    }, [])

    const handleBulkAction = () => {
        if (selectedCampaign.length === 0) {
            Notify.notify("Select alteast one to perform this action")
            return
        }
        setShowConfirmModal(true);

    }

    const confirmBulkAction = () => {
        const payload = { campaignIds: selectedCampaign.toString(), status: isRunning ? STATUS_MAP.Pausing : STATUS_MAP.Resuming, actionOn: isSubOrSuperAdmin ? "superadmin" : "user" };
        Fetcher.post(`${apiPath}/api/obd/bulk/resume/paused/campaigns`, {
            method: "POST",
            headers: { "Content-type": "application/json" },
            body: JSON.stringify(payload)
        }).then(res => {
            if (res.message) {
                Notify.notify(res.message)
                getCampaigns();
                setSelectedCampaign([]);
            }
        })
            .catch(() => Notify.notify("Failed to perform bulk action"))
            .finally(() => setShowConfirmModal(false));
    }
    const columns = [
        { dataField: "campaignId", text: "Campaign ID", sort: true },
        { dataField: "campaignName", text: "Campaign Name", sort: true },
        {
            dataField: "campaignStatus",
            text: "Campaign Status",
            formatter: (cell) => {
                const running = cell?.toLowerCase() === "running";
                return (
                    <span className="d-flex align-items-center gap-2">
                        <i className={`fa ${running ? "fa-play-circle text-success" : "fa-pause-circle text-warning"}`}></i>
                        {cell}
                    </span>
                );
            }
        },

    ];

    const data = useMemo(() => {
        return campaignList.map((c) => ({ ...c, campaignStatus: STATUS_CODE_TO_LABEL[c.campaignStatus] ?? "" }))
    }, [campaignList]);

    const { SearchBar, ClearSearchButton } = Search;
    const defaultSorted = [{
        dataField: 'campaignId',
        order: 'desc'
    }];

    const paginationOption = {
        custom: true,
        totalSize: data.length,
        sizePerPage: pageSize,
        sizePerPageList: [10, 25, 50, 100],
        onSizePerPageChange: setPageSize
    };

    // `rows` given to onSelectAll by the table is only the CURRENT PAGE, and
    // ToolkitProvider's baseProps.data is always the full UNFILTERED data (not
    // search-matched). So selectRow is built from `searchedData`, computed by
    // re-running the table's own search algorithm against the full `data` --
    // see filterTableDataBySearch usage below.
    const buildSelectRow = (searchedData) => ({
        mode: 'checkbox',
        clickToSelect: true,
        selected: selectedCampaign,
        onSelect: (row, isSelect) => {
            setSelectedCampaign(prev => isSelect
                ? [...prev, row.campaignId]
                : prev.filter(id => id !== row.campaignId)
            );
        },
        onSelectAll: (isSelect) => {
            setSelectedCampaign(isSelect ? searchedData.map(c => c.campaignId) : []);
        },
        selectColumnStyle: { paddingLeft: '12px' },
        headerColumnStyle: { paddingLeft: '12px' },
    });
    return (
        <Card>
            <Card.Header>Campaign Operations</Card.Header>
            <Card.Body>
                <div className="d-flex justify-content-between align-items-end mb-3">

                    <div className="d-flex align-items-end gap-2">
                        <Form className="add-campaign-form" onSubmit={handleSearch}>
                            <Row className="align-items-end g-2">
                                <Col md="auto">
                                    <Form.Group>
                                        <Form.Label>Select Campaigns</Form.Label>
                                        <Form.Select
                                            value={selectedFilter}
                                            onChange={handleSelect}
                                        >
                                            <option value="running">Running</option>
                                            <option value="paused">Paused</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md="auto">
                                    <Button type="submit" className='mb-3'>Search</Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <div className="d-flex align-items-end mb-3">
                        {Boolean(selectedCampaign.length) &&
                            <Button variant="primary"
                                onClick={handleBulkAction}
                                disabled={!selectedCampaign.length}
                            >
                                {actionLabel} Selected Campaign {selectedCampaign.length}
                            </Button>}
                    </div>

                </div>
                {isRunning && (
                    <Alert variant="warning" className="py-2 px-3">
                        <i className="fa fa-exclamation-triangle me-2"></i>This does not include continuous campaigns.
                    </Alert>
                )}
                <div className="user-list-table-cont">
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <PaginationProvider pagination={paginationFactory(paginationOption)}>
                            {
                                ({
                                    paginationProps,
                                    paginationTableProps
                                }) => (
                                    <ToolkitProvider
                                        keyField="campaignId"
                                        data={data}
                                        columns={columns}
                                        search
                                        bootstrap4
                                    >
                                        {
                                            props => (
                                                <div className="">
                                                    Show <SizePerPageDropdownStandalone className="search-dropdown" {...paginationProps} /> items
                                                    <span className="search-cont"><SearchBar {...props.searchProps} /></span>
                                                    <ClearSearchButton className="btn-primary btn-round" {...props.searchProps} />
                                                    <hr />
                                                    <BootstrapTable
                                                        bordered={true}
                                                        defaultSorted={defaultSorted}
                                                        striped={true}
                                                        condensed
                                                        wrapperClasses="table-responsive"
                                                        classes="userCampaignList-table"
                                                        {...props.baseProps}
                                                        {...paginationTableProps}
                                                        noDataIndication="No Data to show"
                                                        selectRow={buildSelectRow(filterTableDataBySearch(data, columns, props.searchProps.searchText))}
                                                    />
                                                    <PaginationListStandalone {...paginationProps} />
                                                </div>
                                            )
                                        }
                                    </ToolkitProvider>
                                )
                            }
                        </PaginationProvider>
                    )}
                </div>
            </Card.Body>
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm {actionLabel}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to {actionLabel.toLowerCase()} {selectedCampaign.length} selected campaign(s)?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={confirmBulkAction}>{actionLabel}</Button>
                </Modal.Footer>
            </Modal>
        </Card >
    )
}

export default CampaignOperation